import type { ComponentChildren, JSX, Ref } from 'preact';
import { useId } from 'preact/hooks';
import { Check, Minus } from 'lucide-preact';
import { cn } from '@/lib/cn';

export interface CheckboxProps extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ComponentChildren;
  description?: ComponentChildren;
  indeterminate?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}

export function Checkbox({
  label,
  description,
  indeterminate,
  className,
  id,
  disabled,
  checked,
  inputRef,
  ...rest
}: CheckboxProps) {
  const name = typeof rest.name === 'string' ? rest.name : 'x';
  // Estable entre renders: con Math.random() el id (y el htmlFor) cambiaba en
  // cada render y el DOM se reescribía sin necesidad.
  const generatedId = useId();
  const autoId = id ?? `chk-${name}-${generatedId}`;
  return (
    <label
      htmlFor={autoId}
      className={cn(
        'group flex items-start gap-2.5 cursor-pointer select-none',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <span className="relative mt-[2px] flex h-4 w-4 shrink-0 items-center justify-center">
        <input
          ref={(el) => {
            if (typeof inputRef === 'function') inputRef(el);
            else if (inputRef && typeof inputRef === 'object') inputRef.current = el;
            if (el) el.indeterminate = !!indeterminate;
          }}
          id={autoId}
          type="checkbox"
          disabled={disabled}
          checked={checked}
          className="peer absolute inset-0 h-4 w-4 appearance-none rounded-xs border border-border-subtle bg-surface transition-colors duration-med ease-snappy hover:border-border-strong checked:border-brand checked:bg-brand indeterminate:border-brand indeterminate:bg-brand focus-visible:outline-none focus-visible:shadow-focus cursor-pointer disabled:cursor-not-allowed"
          {...rest}
        />
        {indeterminate ? (
          <Minus
            size={12}
            strokeWidth={3}
            className="pointer-events-none absolute text-brand-contrast opacity-0 peer-indeterminate:opacity-100"
            aria-hidden="true"
          />
        ) : (
          <Check
            size={12}
            strokeWidth={3}
            className="pointer-events-none absolute text-brand-contrast opacity-0 peer-checked:opacity-100"
            aria-hidden="true"
          />
        )}
      </span>
      {(label || description) && (
        <span className="flex flex-col leading-tight">
          {label ? <span className="text-13 text-fg-strong">{label}</span> : null}
          {description ? <span className="text-xs text-fg-muted">{description}</span> : null}
        </span>
      )}
    </label>
  );
}
